import { Link, matchPath, useLocation } from "react-router-dom";
import { LuPhone, LuMessageCircle, LuMapPin } from "react-icons/lu";
import { FaFacebookSquare, FaInstagram, FaGooglePlay } from "react-icons/fa";
import BrandLogo from "./BrandLogo";
import { WHATSAPP_NUMBER, PHONE_DISPLAY, PLAY_STORE_URL } from "../config/contact";
import { TOP_CATEGORIES } from "../config/topCategories";
import { useCiT } from "./ci/i18n";
import ReportProblem from "./ci/ReportProblem";

/**
 * Footer — design canvas ke hisaab se navy, 4 column (Brand · Shop · Orders · Company).
 * Desktop aur mobile dono ke liye ek hi component; mobile par column stack ho jaate hain.
 * Profile/product screens par footer nahi dikhta (purana rule waisa hi).
 */
const Footer = () => {
  const location = useLocation();
  const t = useCiT();

  const excludeRoutes = [
    // "/product/:id/:name" jaan-boojh kar hataya: footer ke category links hi
    // site ke asli internal links hain, aur product page unhi ka sabse zyada
    // mohtaaj hai.
    "/profile",
    "/profile/*",
    "/cart",
    "/checkout",
    "/searchpage",
  ];
  const isExcluded = excludeRoutes.some((route) => matchPath(route, location.pathname));
  if (isExcluded) return null;

  const col = "flex flex-col gap-2 text-[14px] text-[#b8c2cf]";
  const head = "mb-1 font-semibold text-white";
  const link = "hover:text-white";

  return (
    <footer id="footer" className="mt-10 bg-ci-navy text-[#b8c2cf] pb-[70px] sm:pb-0">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-8 px-5 py-10 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-3">
          <BrandLogo variant="light" markSize={44} wordmarkWidth={185} showTagline={false} />
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#8fa0b8]">
            The Iron Junction · Powered by LohaWala
          </span>
          <span className="text-[14px]">
            Construction material supplier, Rudrapur, Uttarakhand. Steel, cement, roofing,
            pipes, hardware — seedha godown se.
          </span>
          <span className="flex items-center gap-2 text-white">
            <LuMapPin size={16} /> Rudrapur, Udham Singh Nagar, Uttarakhand
          </span>
          {PHONE_DISPLAY ? (
            <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white">
              <LuPhone size={16} /> {PHONE_DISPLAY}
            </a>
          ) : null}
          {WHATSAPP_NUMBER ? (
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-white"
            >
              <LuMessageCircle size={16} /> WhatsApp for bulk quote
            </a>
          ) : null}
        </div>

        <div className={col}>
          <b className={head}>{t("Shop")}</b>
          <Link className={link} to="/rate-today">{t("Aaj ka rate — poori list")}</Link>
          <Link className={link} to="/items">{t("All products")}</Link>
          <Link className={link} to="/categories">{t("All categories")}</Link>
          <Link className={link} to="/company">{t("Brands / companies")}</Link>
          <Link className={link} to="/cart">{t("Cart")}</Link>
        </div>

        <div className={col}>
          <b className={head}>{t("Orders")}</b>
          <Link className={link} to="/order">{t("Track order")}</Link>
          <Link className={link} to="/profile/my-orders">{t("My orders")}</Link>
          <Link className={link} to="/profile/addresses">{t("Delivery addresses")}</Link>
          <Link className={link} to="/signup">{t("Dealer / contractor account")}</Link>
          <Link className={link} to="/faq">{t("FAQs")}</Link>
        </div>

        <div className={col}>
          <b className={head}>{t("Company")}</b>
          <Link className={link} to="/aboutus">{t("About Chawla Ispat")}</Link>
          <Link className={link} to="/contactus">{t("Contact & godown addresses")}</Link>
          <a className={link} href="https://lohawalla.com" target="_blank" rel="noreferrer">
            LohaWala ERP
          </a>
          <Link className={link} to="/privacy-policy">{t("Privacy policy")}</Link>
          <Link className={link} to="/delete-account">{t("Delete account")}</Link>
          <div className="mt-2 flex items-center gap-3 text-white">
            <a
              aria-label="Instagram"
              href="https://www.instagram.com/chawlaispat"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ci-orange"
            >
              <FaInstagram size={20} />
            </a>
            <a
              aria-label="Facebook"
              href="https://www.facebook.com/chawlaispatrdp"
              target="_blank"
              rel="noreferrer"
              className="hover:text-ci-orange"
            >
              <FaFacebookSquare size={20} />
            </a>
            {PLAY_STORE_URL ? (
              <a
                aria-label="Android app"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 rounded border border-[#34435a] px-2 py-1 text-[12px] hover:border-white"
              >
                <FaGooglePlay size={14} /> Android app
              </a>
            ) : null}
          </div>
        </div>
      </div>
      {/* Popular categories — har page se category tak seedha <a href>, crawler ke liye */}
      <div className="border-t border-[#34435a]">
        <div className="mx-auto max-w-[1280px] px-5 py-4">
          <b className="mb-2 block text-[13px] font-semibold text-white">
            {t("Popular categories")}
          </b>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[14px]">
            {TOP_CATEGORIES.map((c) => (
              <Link key={c.slug} className={link} to={`/c/${c.slug}`}>
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-[#34435a]">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-1 px-5 py-3.5 text-[13px] sm:flex-row sm:justify-between">
          <span>© {new Date().getFullYear()} Chawla Ispat Pvt. Ltd.</span>
          <span>{t("Rates incl. GST · Prices change daily")}</span>
          <ReportProblem />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
